import { canonicalPath, lineCells, type Board } from './board';
import type { LanguageProfile } from './language';
import { graphemes, normalizeWord } from './language';

export const rulesVersion = 'classic-3';
export const unusedHintPoints = 25;
export interface ScoreEntry { words: string[]; base: number; target: number; special: number; completion: number; unusedHints?: number; hintCount?: number; adHintCount?: number; total: number }
export interface ReadingReward { word: string; cells: number[]; direction: 'forward' | 'reverse' }
export interface AcceptedPath { key: string; cells: number[]; words: string[] }
export interface GameState {
  schemaVersion: 1;
  rulesVersion: string;
  board: Board;
  credited: string[];
  found: string[];
  accepted: AcceptedPath[];
  ledger: ScoreEntry[];
  score: number;
  hints: number;
  assisted: boolean;
  status: 'playing' | 'completed';
}
export interface Lexicon { language: LanguageProfile; permitted: Set<string>; blocked: Set<string> }
export type Outcome = 'accepted' | 'invalid' | 'repeat' | 'unknown' | 'stale' | 'completed';
export interface PlayResult { state: GameState; outcome: Outcome; words: string[]; readings: ReadingReward[]; entry?: ScoreEntry }

export function targetScore(state: GameState): number {
  const words = new Set(state.board.targets.map(target => target.word));
  let total = 100 + 5 * words.size;
  for (const word of words) {
    const letters = graphemes(word, state.board.locale), reverse = [...letters].reverse().join('');
    total += letters.length * 10 + 20 + (word === reverse ? 15 : 0);
    if (word !== reverse && words.has(reverse) && word < reverse) total += 25;
  }
  return total + (state.rulesVersion !== 'classic-1' ? 3 * unusedHintPoints : 0);
}

export function createGame(board: Board): GameState {
  return { schemaVersion: 1, rulesVersion, board, credited: [], found: [], accepted: [], ledger: [], score: 0, hints: 3, assisted: false, status: 'playing' };
}

export function submitSelection(state: GameState, start: number, end: number, lexicon: Lexicon, boardId = state.board.id, extraHints = 0): PlayResult {
  const unchanged = (outcome: Outcome): PlayResult => ({ state, outcome, words: [], readings: [] });
  if (boardId !== state.board.id) return unchanged('stale');
  if (state.status === 'completed') return unchanged('completed');
  const cells = lineCells(state.board.rows, state.board.columns, start, end);
  if (!cells) return unchanged('invalid');
  const key = canonicalPath(cells);
  if (state.accepted.some(path => path.key === key)) return unchanged('repeat');
  const forward = normalizeWord(cells.map(cell => state.board.letters[cell]).join(''), lexicon.language);
  const reverse = normalizeWord([...cells].reverse().map(cell => state.board.letters[cell]).join(''), lexicon.language);
  const valid = [...new Set([forward, reverse])].filter(word => lexicon.permitted.has(word) && !lexicon.blocked.has(word));
  const words = valid.filter(word => !state.credited.includes(word));
  if (words.length === 0) return unchanged(valid.length > 0 ? 'repeat' : 'unknown');
  const newTargets = words.filter(word => state.board.targets.some(target => target.word === word) && !state.found.includes(word));
  const found = [...state.found, ...newTargets];
  const completed = found.length === state.board.targets.length;
  const base = words.length * cells.length * 10;
  const target = newTargets.length * 20;
  const special = forward === reverse ? 15 : words.length === 2 ? 25 : 0;
  const completion = completed ? 100 + 5 * state.board.targets.length : 0;
  const adHintCount = completed && state.rulesVersion === 'classic-3' && Number.isSafeInteger(extraHints) && extraHints >= 0 ? extraHints : 0;
  const hintCount = (completed && state.rulesVersion !== 'classic-1' ? Math.min(3, state.hints) : 0) + adHintCount;
  const unusedHints = hintCount * unusedHintPoints;
  const entry = { words, base, target, special, completion, hintCount, adHintCount, unusedHints, total: base + target + special + completion + unusedHints };
  const readings: ReadingReward[] = [];
  if (words.includes(forward)) readings.push({ word: forward, cells, direction: 'forward' });
  if (words.includes(reverse)) readings.push({ word: reverse, cells: [...cells].reverse(), direction: 'reverse' });
  return {
    state: { ...state, credited: [...state.credited, ...words], found, accepted: [...state.accepted, { key, cells, words }], ledger: [...state.ledger, entry], score: state.score + entry.total, hints: completed && state.rulesVersion !== 'classic-1' ? 0 : state.hints, status: completed ? 'completed' : 'playing' },
    outcome: 'accepted', words, readings, entry,
  };
}

export function useHint(state: GameState): { state: GameState; cells: number[] } | null {
  if (state.hints < 1 || state.status !== 'playing') return null;
  const target = state.board.targets.find(word => !state.found.includes(word.word));
  if (!target) return null;
  return { state: { ...state, hints: state.hints - 1, assisted: true }, cells: target.cells };
}
